import { useEffect, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { Link } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useConfirm } from '../../components/ConfirmDialog';
import type { RlcEnvironment, RlcListChange, RlcListChangePart, RlcMetricEvent, RlcModelService, RlcPopupReconstruction, RlcScanResult, RlcScanViolation, RlcServiceConfig, RlcSetting } from '../../types';

const LIST_LIMIT = 500;

const inputClass = 'bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100';
const primaryButton =
  'px-3 py-1.5 text-sm rounded bg-blue-900/50 text-blue-100 border border-blue-800 hover:bg-blue-900 disabled:opacity-50';
const plainButton =
  'px-3 py-1.5 text-sm rounded bg-gray-800 text-gray-100 border border-gray-700 hover:bg-gray-700 disabled:opacity-50';

export function Section({ title, badge, children }: { title: string; badge?: string; children: React.ReactNode }) {
  return (
    <section className="bg-gray-900 border border-gray-800 rounded p-4">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-base font-semibold text-gray-100">{title}</h2>
        {badge && (
          <span
            className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded border ${
              badge === 'Live'
                ? 'bg-emerald-900/40 text-emerald-200 border-emerald-700/60'
                : 'bg-gray-800 text-gray-300 border-gray-700'
            }`}
          >
            {badge}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

export function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded border border-red-800 bg-red-900/20 px-4 py-3 text-sm text-red-200">{children}</div>
  );
}

function NotConnected({ what, envKey }: { what: string; envKey: string }) {
  return (
    <p className="text-sm text-gray-400 max-w-3xl">
      Not connected. To show {what} for this environment, mint a console token in its api-server and add it to
      the console's <code className="text-xs">RLC_ENVIRONMENT_TOKENS</code> setting under the key{' '}
      <code className="text-xs">{envKey}</code>.
    </p>
  );
}

function parseTerms(text: string) {
  return text.split('\n').map((t) => t.trim()).filter(Boolean);
}

/** Rows of a CSV file (RFC 4180: quoted fields may hold commas, quotes and line breaks). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  const src = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field); rows.push(row); row = []; field = '';
    } else field += c;
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((f) => f.trim() !== ''));
}

const countOf = (terms: string[], count?: number) => count ?? terms.length;

// ─── Running now ─────────────────────────────────────────────────────────────

const shortImage = (ref: string) => ref.replace(/^[^/]+\.[^/]+\//, '');

export function RunningSection({ env }: { env: RlcEnvironment }) {
  const { data, error } = useSWR(
    ['admin/environments/running', env.key],
    () => adminApi.getEnvironmentRunning(env.key),
    { refreshInterval: 60_000, revalidateOnFocus: false }
  );

  return (
    <Section title="Running now" badge="Live">
      {error ? (
        <ErrorNote>Could not read what is running: {error.message}</ErrorNote>
      ) : !data ? (
        <p className="text-sm text-gray-500">Reading the cluster…</p>
      ) : !data.available ? (
        <p className="text-sm text-gray-400 max-w-3xl">
          The console cannot read this environment's pods ({data.error}). It needs permission to list pods in
          namespace <code className="text-xs">{env.deployment.namespace}</code>.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500 max-w-3xl">
            Read from the pods in namespace <code>{data.namespace}</code>. A tag such as “latest” can point at any build,
            so each row shows the exact image that was pulled and every tag the registry has on it.
          </p>
          {data.running?.length === 0 ? (
            <p className="text-sm text-gray-400 mt-3">No pods found for this release.</p>
          ) : (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-400 text-left">
                    <th className="py-1.5 pr-4 font-medium">Service</th>
                    <th className="py-1.5 pr-4 font-medium">Image the pods run</th>
                    <th className="py-1.5 pr-4 font-medium">That exact build</th>
                    <th className="py-1.5 pr-4 font-medium">Pods</th>
                    <th className="py-1.5 font-medium">Running since</th>
                  </tr>
                </thead>
                <tbody>
                  {data.running?.map((r) => {
                    const otherTags = (r.registryTags ?? []).filter((t) => !r.image.endsWith(`:${t}`));
                    const healthy = r.ready === r.pods && !r.waiting;
                    return (
                      <tr key={`${r.image}-${r.digest}`} className="border-b border-gray-800/60 align-top">
                        <td className="py-2 pr-4 text-gray-100 whitespace-nowrap">{r.component ?? r.container}</td>
                        <td className="py-2 pr-4">
                          <code className="text-xs text-gray-200 break-all">{shortImage(r.image)}</code>
                          {r.declaredImage && !r.matchesDeclared && (
                            <div className="text-xs text-amber-300 mt-1">
                              The catalog says <code>{shortImage(r.declaredImage)}</code>
                            </div>
                          )}
                          {!r.component && <div className="text-xs text-amber-300 mt-1">Not in the catalog</div>}
                        </td>
                        <td className="py-2 pr-4 text-xs">
                          {otherTags.length > 0 ? (
                            <div className="text-gray-200">also tagged {otherTags.map((t) => <code key={t} className="mr-1.5">{t}</code>)}</div>
                          ) : r.registryTags ? (
                            <div className="text-gray-500">no other tag</div>
                          ) : (
                            <div className="text-gray-500">tags unavailable{r.registryError ? ` (${r.registryError})` : ''}</div>
                          )}
                          {r.pushedAt && <div className="text-gray-500 mt-0.5">built {new Date(r.pushedAt).toLocaleDateString()}</div>}
                          {r.digest && <code className="block text-gray-600 mt-0.5" title={r.digest}>{r.digest.slice(7, 19)}</code>}
                        </td>
                        <td className={`py-2 pr-4 text-xs whitespace-nowrap ${healthy ? 'text-emerald-300' : 'text-red-300'}`}>
                          {r.ready} of {r.pods} ready
                          {r.waiting && <div>{r.waiting}</div>}
                          {r.restarts > 0 && <div className="text-gray-500">{r.restarts.toLocaleString()} restarts</div>}
                        </td>
                        <td className="py-2 text-gray-500 text-xs whitespace-nowrap">{r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

// ─── Model services ──────────────────────────────────────────────────────────

/**
 * One card per model service (NER, code detector). The services have no
 * version endpoint, so the model and server version come from a real call.
 */
export function ModelServiceSections({ env }: { env: RlcEnvironment }) {
  const { data, error } = useSWR(
    ['admin/environments/models', env.key],
    () => adminApi.getEnvironmentModels(env.key),
    { refreshInterval: 60_000, revalidateOnFocus: false }
  );

  if (error) {
    return (
      <Section title="Model services" badge="Live">
        <ErrorNote>Could not check the model services: {error.message}</ErrorNote>
      </Section>
    );
  }
  if (!data) {
    return (
      <Section title="Model services" badge="Live">
        <p className="text-sm text-gray-500">Asking the model services…</p>
      </Section>
    );
  }
  return (
    <>
      {data.services.map((s) => (
        <ModelServiceCard key={s.key} service={s} />
      ))}
    </>
  );
}

function ModelServiceCard({ service: s }: { service: RlcModelService }) {
  if (s.deployed === false) {
    return (
      <Section title={s.name}>
        <div className="flex items-center gap-2 text-gray-300">
          <span className="w-2 h-2 rounded-full shrink-0 bg-gray-600" />
          <span>Not deployed in this environment</span>
        </div>
        <p className="text-xs text-gray-500 mt-3">No pods for this service exist here, so the API server runs without it.</p>
      </Section>
    );
  }

  const pods = s.images.reduce((n, i) => n + i.pods, 0);
  const ready = s.images.reduce((n, i) => n + i.ready, 0);
  const ok = s.live?.ok === true;
  return (
    <Section title={s.name} badge="Live">
      <div className="flex items-center gap-2 text-gray-100">
        <span className={`w-2 h-2 rounded-full shrink-0 ${ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
        <span>{ok ? 'Responding' : 'Not responding'}</span>
        {!ok && s.live?.error && <span className="text-xs text-red-300">({s.live.error})</span>}
      </div>
      <dl className="mt-3 text-sm">
        {(
          [
            ['Model', s.live?.model ? <code key="m" className="text-xs">{s.live.model}</code> : 'unknown'],
            ['Server version', s.live?.server ?? 'unknown'],
            ['Image', s.images.length === 0 ? 'unknown (pods could not be read)' : (
              <div key="i" className="space-y-1.5">
                {s.images.map((img) => {
                  const otherTags = (img.registryTags ?? []).filter((t) => !img.image.endsWith(`:${t}`));
                  return (
                    <div key={`${img.image}-${img.digest}`}>
                      <code className="text-xs break-all">{shortImage(img.image)}</code>
                      {otherTags.length > 0 && (
                        <div className="text-xs text-gray-500">also tagged {otherTags.map((t) => <code key={t} className="mr-1.5">{t}</code>)}</div>
                      )}
                      {img.pushedAt && <div className="text-xs text-gray-500">built {new Date(img.pushedAt).toLocaleDateString()}</div>}
                    </div>
                  );
                })}
              </div>
            )],
            ['Pods', pods === 0 ? 'unknown' : <span key="p" className={ready === pods ? '' : 'text-red-300'}>{ready} of {pods} ready</span>],
            ['Response time', s.live ? `${s.live.latencyMs.toLocaleString()} ms` : 'unknown'],
          ] as [string, React.ReactNode][]
        ).map(([label, value]) => (
          <div key={label} className="flex gap-4 py-1.5 border-t border-gray-800">
            <dt className="w-40 shrink-0 text-gray-500">{label}</dt>
            <dd className="text-gray-200 min-w-0 break-words">{value}</dd>
          </div>
        ))}
      </dl>
      {s.images.length > 1 ? (
        <p className="text-xs text-amber-300 mt-3">
          The pods run {s.images.length} different images. The model and server version come from whichever pod answered.
        </p>
      ) : (
        <p className="text-xs text-gray-500 mt-3">
          Model and server version are what the service reported in a test call just now. The image is read from its pods.
        </p>
      )}
    </Section>
  );
}

// ─── Internal domains ────────────────────────────────────────────────────────

/**
 * The domains the add-in treats as internal. A send is checked only when at
 * least one recipient (To, Cc or Bcc) is on a domain NOT in this list.
 */
export function InternalDomainsSection({ env }: { env: RlcEnvironment }) {
  const { data, error, mutate } = useSWR(
    ['admin/environments/internal-domains', env.key],
    () => adminApi.getEnvironmentInternalDomains(env.key),
    { refreshInterval: 60_000, revalidateOnFocus: false }
  );
  const history = useSWR(
    data?.editable ? ['admin/environments/internal-domains/changes', env.key] : null,
    () => adminApi.listEnvironmentInternalDomainChanges(env.key),
    { revalidateOnFocus: false }
  );
  const confirm = useConfirm();
  const [filter, setFilter] = useState('');
  const [address, setAddress] = useState('');
  const [editing, setEditing] = useState(false);
  const [toAdd, setToAdd] = useState('');
  const [toRemove, setToRemove] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<RlcListChange | null>(null);

  const domains = data?.domains ?? [];
  const shown = filter.trim() ? domains.filter((d) => d.includes(filter.trim().toLowerCase())) : domains;
  // Same rule as the add-in's isExternalEmail: exact, case-insensitive match on everything after the @.
  const testDomain = address.includes('@') ? address.split('@')[1].trim().toLowerCase() : address.trim().toLowerCase();
  const internal = testDomain ? domains.includes(testDomain) : null;
  const adds = toAdd.split(/[\s,;]+/).map((d) => d.trim().toLowerCase().replace(/^.*@/, '')).filter(Boolean);

  const stopEditing = () => {
    setEditing(false);
    setToAdd('');
    setToRemove(new Set());
    setSaveError(null);
  };
  const toggleRemove = (d: string) =>
    setToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });

  const save = async () => {
    if (!data?.fingerprint) return;
    const removes = [...toRemove];
    const ok = await confirm({
      title: `Change the internal domains on ${env.name}?`,
      message: (
        <div className="space-y-2 text-sm">
          <p>
            Mail to these domains is not checked. Outlook users pick up the change the next time their add-in refreshes
            its copy, within 12 hours. The list goes from {domains.length} domains to about {domains.length + adds.length - removes.length}.
          </p>
          {adds.length > 0 && <TermSummary label="Add (mail to these stops being checked)" terms={adds} />}
          {removes.length > 0 && <TermSummary label="Remove (mail to these starts being checked)" terms={removes} />}
        </div>
      ),
      confirmLabel: 'Save changes',
    });
    if (!ok) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { change } = await adminApi.changeEnvironmentInternalDomains(env.key, { fingerprint: data.fingerprint, add: adds, remove: removes });
      setSaved(change);
      stopEditing();
      await Promise.all([mutate(adminApi.getEnvironmentInternalDomains(env.key, true), { revalidate: false }), history.mutate()]);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Internal domains" badge="Live">
      <p className="text-xs text-gray-500 max-w-3xl">
        The add-in checks a message only when at least one recipient (To, Cc or Bcc) is on a domain not in this list.
        Mail to internal recipients only is sent without a check. Matching is exact: a subdomain such as
        notes.example.com must be listed on its own. Read from <code>/api/internalDomains</code> the way the add-in reads it.
      </p>
      {error ? (
        <ErrorNote>Could not read the internal domains: {error.message}</ErrorNote>
      ) : !data ? (
        <p className="text-sm text-gray-500 mt-3">Reading the list…</p>
      ) : !data.available ? (
        <p className="text-sm text-gray-400 mt-3">Not available: {data.error}</p>
      ) : (
        <>
          <dl className="mt-3 text-sm">
            {(
              [
                ['Domains', `${domains.length.toLocaleString()}`],
                ['List version', data.version ? <code key="v" className="text-xs">{data.version}</code> : 'unknown'],
                [
                  'Used by the live add-in',
                  data.addinReadsList === true ? (
                    <span key="u">Yes. The add-in fetches this list and keeps it for up to 12 hours.</span>
                  ) : data.addinReadsList === false ? (
                    <span key="u" className="text-amber-300">
                      No. This add-in build predates the configurable list and uses the domains bundled into it, so
                      changing this list has no effect here until the add-in is updated.
                    </span>
                  ) : (
                    <span key="u" className="text-gray-500">unknown (could not read the add-in's script)</span>
                  ),
                ],
              ] as [string, React.ReactNode][]
            ).map(([label, value]) => (
              <div key={label} className="flex gap-4 py-1.5 border-t border-gray-800">
                <dt className="w-40 shrink-0 text-gray-500">{label}</dt>
                <dd className="text-gray-200 min-w-0 break-words">{value}</dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Is this address internal? name@domain.com"
              className={`${inputClass} w-80`}
            />
            {internal != null && (
              <span className={`text-sm ${internal ? 'text-emerald-300' : 'text-amber-300'}`}>
                {internal ? `Internal: mail only to ${testDomain} is not checked` : `External: mail to ${testDomain} is checked`}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter domains" className={`${inputClass} w-56`} />
            <span className="text-xs text-gray-500">
              {shown.length === domains.length ? `${domains.length} domains` : `${shown.length} of ${domains.length}`}
            </span>
            <div className="ml-auto flex items-center gap-2">
              {data.editable && !editing && (
                <button className={plainButton} onClick={() => { setSaved(null); setEditing(true); }}>Edit domains</button>
              )}
              {!data.editable && <span className="text-xs text-gray-500">Read-only here</span>}
            </div>
          </div>
          {editing && (
            <div className="mt-3 rounded border border-gray-800 bg-gray-950 p-3 space-y-2">
              <p className="text-xs text-gray-400">Click a domain below to mark it for removal. Add domains here, separated by spaces, commas or new lines; a pasted address keeps only its domain.</p>
              <textarea
                value={toAdd}
                onChange={(e) => setToAdd(e.target.value)}
                rows={2}
                placeholder="example.com, notes.example.com"
                className={`${inputClass} w-full font-mono`}
              />
              <div className="flex items-center gap-2">
                <button className={primaryButton} disabled={saving || (adds.length === 0 && toRemove.size === 0)} onClick={save}>
                  {saving ? 'Saving…' : `Save (${adds.length} to add, ${toRemove.size} to remove)`}
                </button>
                <button className={plainButton} disabled={saving} onClick={stopEditing}>Cancel</button>
              </div>
              {saveError && <ErrorNote>{saveError}</ErrorNote>}
            </div>
          )}
          {saved && (
            <p className={`text-sm mt-3 ${saved.status === 'applied' ? 'text-emerald-300' : 'text-amber-300'}`}>
              {saved.status === 'applied'
                ? `Saved and read back: ${saved.added.length} added, ${saved.removed.length} removed. Outlook users get it within 12 hours.`
                : `Sent, but ${saved.error ?? 'the read-back did not match'}. Reload to check.`}
            </p>
          )}
          <div className="mt-2 max-h-64 overflow-y-auto border border-gray-800 rounded p-2 flex flex-wrap gap-1.5">
            {shown.map((d) =>
              editing ? (
                <button
                  key={d}
                  onClick={() => toggleRemove(d)}
                  title={toRemove.has(d) ? 'Keep' : 'Remove'}
                  className={`text-xs font-mono rounded px-1.5 py-0.5 ${toRemove.has(d) ? 'bg-red-900/50 text-red-200 line-through' : 'bg-gray-800 text-gray-200 hover:bg-gray-700'}`}
                >
                  {d} {toRemove.has(d) ? '↺' : '×'}
                </button>
              ) : (
                <code key={d} className="text-xs bg-gray-800 text-gray-200 rounded px-1.5 py-0.5">{d}</code>
              )
            )}
            {shown.length === 0 && <span className="text-xs text-gray-500">No domains match</span>}
          </div>
          {data.editable && (history.data?.changes.length ?? 0) > 0 && (
            <div className="mt-4">
              <div className="text-xs text-gray-500 mb-1">Changes made from the console</div>
              <ul className="text-xs space-y-1">
                {history.data!.changes.map((c) => (
                  <li key={c.id} className="text-gray-300">
                    <span className="text-gray-500">{c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}</span>{' '}
                    {c.actorEmail ?? 'unknown'}:{' '}
                    {c.added.length > 0 && <span className="text-emerald-300">+{c.added.join(', +')} </span>}
                    {c.removed.length > 0 && <span className="text-red-300">−{c.removed.join(', −')} </span>}
                    <span className={c.status === 'applied' ? 'text-gray-500' : 'text-amber-300'}>({c.status})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

// ─── Live configuration ──────────────────────────────────────────────────────

/**
 * The settings each deployment runs with, read from the cluster, with every
 * setting last written by something other than Helm flagged.
 */
export function LiveConfigSection({ env }: { env: RlcEnvironment }) {
  const { data, error } = useSWR(
    ['admin/environments/config', env.key],
    () => adminApi.getEnvironmentConfig(env.key),
    { refreshInterval: 60_000, revalidateOnFocus: false }
  );

  const outside = (data?.services ?? []).filter((s) => s.changedOutsideHelm.length > 0);

  return (
    <Section title="Live configuration" badge="Live">
      {error ? (
        <ErrorNote>Could not read the configuration: {error.message}</ErrorNote>
      ) : !data ? (
        <p className="text-sm text-gray-500">Reading the cluster…</p>
      ) : !data.available ? (
        <p className="text-sm text-gray-400 max-w-3xl">
          The console cannot read this environment's deployments ({data.error}). It needs permission to read
          deployments and configmaps in namespace <code className="text-xs">{env.deployment.namespace}</code>.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500 max-w-3xl">
            The environment variables each deployment in <code>{data.namespace}</code> runs with, read from the cluster.
            Secret values are never read; the row says which Secret they come from. Anything that looks like a
            credential is hidden, and passwords inside URLs are masked.
          </p>
          {outside.length === 0 ? (
            <p className="text-sm text-emerald-300 mt-3">Every setting was last written by Helm.</p>
          ) : (
            <div className="rounded border border-amber-700/60 bg-amber-900/20 px-4 py-3 text-sm text-amber-100 mt-3">
              Some settings were last changed outside Helm. A value set with kubectl survives the next helm upgrade
              unchanged, so put it in the chart's values instead.
              <ul className="list-disc ml-5 mt-2 space-y-1 text-xs">
                {outside.flatMap((s) =>
                  s.changedOutsideHelm.map((c) => (
                    <li key={`${s.deployment}-${c.manager}-${c.at}`}>
                      <span className="font-medium">{s.component}</span>: {c.manager}
                      {c.at && ` on ${new Date(c.at).toLocaleString()}`}
                      {c.env.length > 0 && <> set {c.env.map((e) => e.split(':')[1]).join(', ')}</>}
                      {c.fields.length > 0 && (
                        <>
                          {c.env.length > 0 ? ' and ' : ' set '}
                          {c.fields.slice(0, 6).join(', ')}
                          {c.fields.length > 6 && ` and ${c.fields.length - 6} more`}
                        </>
                      )}
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
          <div className="mt-4 space-y-4">
            {(data.services ?? []).map((s) => (
              <ServiceConfig key={s.deployment} service={s} />
            ))}
          </div>
        </>
      )}
    </Section>
  );
}

function ServiceConfig({ service: s }: { service: RlcServiceConfig }) {
  const changed = new Set(s.changedOutsideHelm.flatMap((c) => c.env));
  return (
    <details className="border border-gray-800 rounded" open={s.component === 'api-server'}>
      <summary className="cursor-pointer px-3 py-2 text-sm text-gray-100 flex flex-wrap items-center gap-x-3">
        <span className="font-medium">{s.component}</span>
        <span className="text-xs text-gray-500">
          {s.replicas ?? '?'} replica{s.replicas === 1 ? '' : 's'}
          {s.chart && ` · chart ${s.chart}`}
          {' · '}
          {s.containers.reduce((n, c) => n + c.settings.length, 0)} settings
        </span>
        {s.changedOutsideHelm.length > 0 && <span className="text-xs text-amber-300">changed outside Helm</span>}
      </summary>
      <div className="px-3 pb-3 overflow-x-auto">
        {s.containers.map((c) => (
          <table key={c.name} className="w-full text-sm mt-2">
            <tbody>
              {c.settings.length === 0 && (
                <tr><td className="py-1.5 text-xs text-gray-500">No environment variables</td></tr>
              )}
              {c.settings.map((x, i) => (
                <SettingRow key={`${x.name}-${i}`} setting={x} changed={changed.has(`${c.name}:${x.name}`)} />
              ))}
            </tbody>
          </table>
        ))}
      </div>
    </details>
  );
}

function SettingRow({ setting: x, changed }: { setting: RlcSetting; changed: boolean }) {
  const source =
    x.source === 'secret' ? 'Secret' : x.source === 'configMap' ? 'ConfigMap' : x.source === 'value' ? 'set directly' : x.source;
  return (
    <tr className="border-t border-gray-800 align-top">
      <td className="py-1.5 pr-4 whitespace-nowrap">
        <code className="text-xs text-gray-200">{x.name}</code>
        {changed && <div className="text-[11px] text-amber-300">set outside Helm</div>}
        {x.duplicate && <div className="text-[11px] text-amber-300">defined twice; the later one wins</div>}
      </td>
      <td className="py-1.5 pr-4 text-xs break-all">
        {x.hidden ? (
          <span className="text-gray-500">hidden</span>
        ) : x.error ? (
          <span className="text-red-300">could not read ({x.error})</span>
        ) : x.value == null ? (
          <span className="text-gray-500">—</span>
        ) : (
          <code className="text-gray-200">{x.value}</code>
        )}
      </td>
      <td className="py-1.5 text-xs text-gray-500">
        {source}
        {x.ref && <div className="break-all">{x.ref}</div>}
      </td>
    </tr>
  );
}

// ─── Restricted list ─────────────────────────────────────────────────────────

export function RestrictedListSection({ env }: { env: RlcEnvironment }) {
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => { setQ(input.trim()); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [input]);

  const { mutate: mutateGlobal } = useSWRConfig();
  const confirm = useConfirm();

  const { data, error, mutate } = useSWR(
    env.restrictedListConnected ? ['admin/environments/restricted-list', env.key, q, offset] : null,
    () => adminApi.getEnvironmentRestrictedList(env.key, { q, limit: LIST_LIMIT, offset }),
    { keepPreviousData: true, revalidateOnFocus: false }
  );
  const history = useSWR(
    env.restrictedListConnected && env.restrictedListEditable ? ['admin/environments/restricted-list/changes', env.key] : null,
    () => adminApi.listEnvironmentRestrictedListChanges(env.key),
    { revalidateOnFocus: false }
  );

  const [editing, setEditing] = useState(false);
  const [toAdd, setToAdd] = useState('');
  const [toRemove, setToRemove] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastChange, setLastChange] = useState<RlcListChange | null>(null);
  const [replacing, setReplacing] = useState(false);

  const list = data?.lists?.[0];
  const adds = parseTerms(toAdd);
  const removes = [...toRemove];
  const pending = adds.length + removes.length;

  const stopEditing = () => {
    setEditing(false);
    setToAdd('');
    setToRemove(new Set());
  };

  const toggleRemove = (term: string) =>
    setToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(term)) next.delete(term);
      else next.add(term);
      return next;
    });

  const save = async () => {
    if (!list?.fingerprint || pending === 0) return;
    const ok = await confirm({
      title: `Change the restricted list on ${env.name}?`,
      message: (
        <div className="space-y-2 text-sm">
          <p>
            This takes effect for everyone scanning against {env.name} straight away. The list goes from{' '}
            {list.total.toLocaleString()} terms to about {(list.total + adds.length - removes.length).toLocaleString()}.
          </p>
          {adds.length > 0 && <TermSummary label="Add" terms={adds} />}
          {removes.length > 0 && <TermSummary label="Remove" terms={removes} />}
        </div>
      ),
      confirmLabel: 'Save changes',
      danger: removes.length > 0,
    });
    if (!ok) return;

    setSaving(true);
    setSaveError(null);
    setLastChange(null);
    try {
      const { change } = await adminApi.changeEnvironmentRestrictedList(env.key, {
        fingerprint: list.fingerprint,
        add: adds,
        remove: removes,
      });
      setLastChange(change);
      stopEditing();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'The change could not be saved.');
    } finally {
      setSaving(false);
      // Reload whatever happened: a refused change usually means the list moved on.
      mutate();
      history.mutate();
      mutateGlobal('admin/environments');
    }
  };

  return (
    <div id="restricted-list">
      <Section title="Restricted list" badge={env.restrictedListConnected ? 'Live' : undefined}>
        {!env.restrictedListConnected ? (
          <NotConnected what="the list" envKey={env.key} />
        ) : error ? (
          <ErrorNote>Could not read the list: {error.message}</ErrorNote>
        ) : !data ? (
          <p className="text-sm text-gray-500">Reading the list…</p>
        ) : data.error ? (
          <ErrorNote>The api-server refused the request: {data.error}</ErrorNote>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-gray-500">
                {data.editable ? 'The' : 'Read-only. The'} list for the organization the console's token belongs to
                {data.fetchedAt && `, read at ${new Date(data.fetchedAt).toLocaleTimeString()}`}.
                {!data.editable && data.editBlockedReason && ` ${data.editBlockedReason}`}
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Search terms…"
                  className={`${inputClass} w-64`}
                />
                {data.editable && !editing && !replacing && (
                  <>
                    <button onClick={() => { setEditing(true); setSaveError(null); setLastChange(null); }} className={plainButton}>
                      Edit list
                    </button>
                    <button onClick={() => { setReplacing(true); setSaveError(null); setLastChange(null); }} className={plainButton}>
                      Replace from file
                    </button>
                  </>
                )}
              </div>
            </div>

            {lastChange && <ChangeResult change={lastChange} />}
            {saveError && <div className="mt-3"><ErrorNote>{saveError}</ErrorNote></div>}
            {replacing && list?.fingerprint && (
              <ReplaceListPanel
                env={env}
                fingerprint={list.fingerprint}
                currentTotal={list.total}
                onDone={(change) => {
                  setReplacing(false);
                  if (change) setLastChange(change);
                  mutate();
                  history.mutate();
                  mutateGlobal('admin/environments');
                }}
              />
            )}

            {data.lists?.length === 0 && <p className="text-sm text-gray-400 mt-3">This organization has no restricted list.</p>}
            {data.lists?.map((l) => (
              <div key={l.id ?? l.name} className="mt-4">
                <div className="text-sm text-gray-100">
                  {l.name || 'Unnamed list'}
                  <span className="text-gray-500 ml-2">
                    {q ? `${l.matched.toLocaleString()} of ${l.total.toLocaleString()} terms match` : `${l.total.toLocaleString()} terms`}
                  </span>
                </div>
                {editing && (
                  <p className="text-xs text-gray-400 mt-1">
                    Select a term to mark it for removal. Marks are kept as you search and change page, and saving
                    always applies them to the whole list, not just the terms shown.
                  </p>
                )}
                <div className="mt-2 max-h-96 overflow-y-auto bg-gray-950 border border-gray-800 rounded p-3 flex flex-wrap gap-1.5">
                  {l.values.map((v, i) =>
                    editing ? (
                      <button
                        key={`${i}-${v}`}
                        onClick={() => toggleRemove(v)}
                        aria-pressed={toRemove.has(v)}
                        title={toRemove.has(v) ? 'Keep this term' : 'Remove this term'}
                        className={`px-2 py-0.5 rounded text-xs border ${
                          toRemove.has(v)
                            ? 'bg-red-900/40 text-red-200 border-red-700 line-through'
                            : 'bg-gray-800 text-gray-200 border-transparent hover:border-red-700'
                        }`}
                      >
                        {v}
                      </button>
                    ) : (
                      <span key={`${i}-${v}`} className="px-2 py-0.5 rounded bg-gray-800 text-gray-200 text-xs">{v}</span>
                    )
                  )}
                  {l.values.length === 0 && <span className="text-xs text-gray-500">No matching terms</span>}
                </div>
                {l.matched > l.values.length && (
                  <div className="flex items-center justify-between text-xs text-gray-400 mt-2">
                    <span>
                      Showing {(l.offset + 1).toLocaleString()}–{(l.offset + l.values.length).toLocaleString()} of{' '}
                      {l.matched.toLocaleString()}{q && ' matching'} terms
                    </span>
                    <div className="flex gap-2">
                      <button disabled={l.offset === 0} onClick={() => setOffset(Math.max(0, l.offset - LIST_LIMIT))} className={plainButton}>Previous</button>
                      <button disabled={l.offset + l.values.length >= l.matched} onClick={() => setOffset(l.offset + LIST_LIMIT)} className={plainButton}>Next</button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {editing && (
              <div className="mt-4 border-t border-gray-800 pt-4">
                <label className="block text-sm text-gray-300">
                  Terms to add, one per line
                  <textarea
                    value={toAdd}
                    onChange={(e) => setToAdd(e.target.value)}
                    rows={4}
                    className={`${inputClass} w-full mt-1 font-mono`}
                    placeholder={'Acme Corp\nProject Falcon'}
                  />
                </label>
                <div className="flex items-center justify-between gap-4 mt-3">
                  <span className="text-sm text-gray-400">
                    {pending === 0 ? 'No changes yet' : `Add ${adds.length} · Remove ${removes.length}`}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={stopEditing} disabled={saving} className={plainButton}>Cancel</button>
                    <button onClick={save} disabled={saving || pending === 0} className={primaryButton}>
                      {saving ? 'Saving…' : 'Review and save'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {history.data && history.data.changes.length > 0 && <ChangeHistory envKey={env.key} changes={history.data.changes} />}
          </>
        )}
      </Section>
    </div>
  );
}

function TermSummary({ label, terms, total }: { label: string; terms: string[]; total?: number }) {
  const shown = terms.slice(0, 12);
  const count = countOf(terms, total);
  if (count === 0) return null;
  return (
    <p>
      <b>{label} {count.toLocaleString()}:</b> {shown.join(', ')}
      {count > shown.length && ` and ${(count - shown.length).toLocaleString()} more`}
    </p>
  );
}

function ChangeResult({ change }: { change: RlcListChange }) {
  const applied = change.status === 'applied';
  return (
    <div
      className={`mt-3 rounded border px-4 py-3 text-sm ${
        applied ? 'bg-emerald-900/20 border-emerald-700/60 text-emerald-100' : 'bg-amber-900/20 border-amber-700/60 text-amber-100'
      }`}
    >
      {applied
        ? `Saved. Added ${countOf(change.added, change.addedCount).toLocaleString()}, removed ${countOf(change.removed, change.removedCount).toLocaleString()}; the list now has ${change.afterCount.toLocaleString()} terms.`
        : `The change was sent but could not be confirmed: ${change.error} Check the list below.`}
      {!!change.alreadyPresent?.length && <div className="text-xs mt-1 opacity-80">Already on the list, not added: {change.alreadyPresent.join(', ')}</div>}
      {!!change.notFound?.length && <div className="text-xs mt-1 opacity-80">Not on the list, not removed: {change.notFound.join(', ')}</div>}
    </div>
  );
}

function ChangeHistory({ envKey, changes }: { envKey: string; changes: RlcListChange[] }) {
  const statusClass: Record<string, string> = {
    applied: 'text-emerald-300',
    failed: 'text-red-300',
    unverified: 'text-amber-300',
    pending: 'text-amber-300',
  };
  const download = (c: RlcListChange, part: RlcListChangePart, label: string) =>
    c.id ? (
      <a key={part} href={adminApi.environmentListChangeUrl(envKey, c.id, part)} className="text-blue-400 hover:underline">
        {label}
      </a>
    ) : null;
  const terms = (c: RlcListChange, part: 'added' | 'removed') => {
    const all = c[part];
    const total = countOf(all, part === 'added' ? c.addedCount : c.removedCount);
    if (total === 0) return '—';
    const shown = all.slice(0, 8);
    return (
      <>
        {shown.join(', ')}
        {total > shown.length && <span className="text-gray-500"> and {(total - shown.length).toLocaleString()} more</span>}
        {total > shown.length && <> · {download(c, part, `all ${total.toLocaleString()}`)}</>}
      </>
    );
  };
  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold text-gray-100">Changes made from the console</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Each one keeps a copy of the list as it was before the change, and newer ones the list that was sent. Download either as a text file.
      </p>
      <table className="w-full text-sm mt-2">
        <thead>
          <tr className="border-b border-gray-800 text-gray-400 text-left">
            <th className="py-1.5 pr-4 font-medium">When</th>
            <th className="py-1.5 pr-4 font-medium">Who</th>
            <th className="py-1.5 pr-4 font-medium">Change</th>
            <th className="py-1.5 pr-4 font-medium">Added</th>
            <th className="py-1.5 pr-4 font-medium">Removed</th>
            <th className="py-1.5 pr-4 font-medium">Terms</th>
            <th className="py-1.5 pr-4 font-medium">Lists</th>
            <th className="py-1.5 font-medium">Result</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((c) => (
            <tr key={c.id} className="border-b border-gray-800/60 align-top">
              <td className="py-1.5 pr-4 text-gray-500 text-xs whitespace-nowrap">{c.createdAt ? new Date(c.createdAt).toLocaleString() : '—'}</td>
              <td className="py-1.5 pr-4 text-gray-300 text-xs">{c.actorEmail || '—'}</td>
              <td className="py-1.5 pr-4 text-gray-300 text-xs">
                {c.mode === 'replace' ? 'Replaced' : 'Edited'}
                {c.source && <div className="text-gray-500 break-all">{c.source}</div>}
              </td>
              <td className="py-1.5 pr-4 text-gray-300 text-xs max-w-xs break-words">{terms(c, 'added')}</td>
              <td className="py-1.5 pr-4 text-gray-300 text-xs max-w-xs break-words">{terms(c, 'removed')}</td>
              <td className="py-1.5 pr-4 text-gray-400 text-xs whitespace-nowrap">{c.beforeCount.toLocaleString()} → {c.afterCount.toLocaleString()}</td>
              <td className="py-1.5 pr-4 text-xs whitespace-nowrap space-x-2">
                {download(c, 'before', 'Before')}
                {c.mode && download(c, 'after', 'After')}
              </td>
              <td className={`py-1.5 text-xs ${statusClass[c.status] ?? 'text-gray-300'}`} title={c.error ?? undefined}>{c.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Replace the whole list with one from a file (CSV or one term per line) or
 * pasted text. Previews the change on the server first, then saves it.
 */
function ReplaceListPanel({
  env,
  fingerprint,
  currentTotal,
  onDone,
}: {
  env: RlcEnvironment;
  fingerprint: string;
  currentTotal: number;
  onDone: (change: RlcListChange | null) => void;
}) {
  const confirm = useConfirm();
  const [source, setSource] = useState('');
  const [rows, setRows] = useState<string[][]>([]);
  const [hasHeader, setHasHeader] = useState(true);
  const [column, setColumn] = useState(0);
  const [preview, setPreview] = useState<RlcListChange | null>(null);
  const [busy, setBusy] = useState<'preview' | 'save' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const width = rows.reduce((n, r) => Math.max(n, r.length), 0);
  const header = hasHeader && rows.length > 0 ? rows[0] : null;
  const terms = (hasHeader ? rows.slice(1) : rows).map((r) => (r[column] ?? '').trim()).filter(Boolean);

  const load = (text: string, name: string, csv: boolean) => {
    const parsed = csv ? parseCsv(text) : parseTerms(text).map((t) => [t]);
    setRows(parsed);
    setSource(name);
    setPreview(null);
    setProblem(null);
    setHasHeader(csv);
    // A CSV with an "entity" / "term" / "name" column is usually the list itself.
    const first = parsed[0]?.map((h) => h.trim().toLowerCase()) ?? [];
    const guess = first.findIndex((h) => ['entity', 'entities', 'term', 'terms', 'name', 'value'].includes(h));
    setColumn(csv && guess >= 0 ? guess : 0);
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    load(await file.text(), file.name, /\.csv$/i.test(file.name));
  };

  const send = async (dryRun: boolean) => {
    setBusy(dryRun ? 'preview' : 'save');
    setProblem(null);
    try {
      const { change } = await adminApi.replaceEnvironmentRestrictedList(env.key, { fingerprint, terms, source: source || undefined, dryRun });
      return change;
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'The list could not be checked.');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    if (!preview) return;
    const ok = await confirm({
      title: `Replace the restricted list on ${env.name}?`,
      message: (
        <div className="space-y-2 text-sm">
          <p>
            This takes effect for everyone scanning against {env.name} straight away. The list goes from{' '}
            {currentTotal.toLocaleString()} terms to {preview.afterCount.toLocaleString()}: {countOf(preview.added, preview.addedCount).toLocaleString()} added,{' '}
            {countOf(preview.removed, preview.removedCount).toLocaleString()} removed. The current list is saved with the change.
          </p>
        </div>
      ),
      confirmLabel: 'Replace list',
      danger: true,
    });
    if (!ok) return;
    const change = await send(false);
    if (change) onDone(change);
  };

  return (
    <div className="mt-4 border border-gray-800 rounded p-4 bg-gray-950/40">
      <h3 className="text-sm font-semibold text-gray-100">Replace the whole list</h3>
      <p className="text-xs text-gray-400 mt-1 max-w-3xl">
        Choose a CSV file (pick the column that holds the terms) or a text file with one term per line, or paste the terms.
        Blank entries are skipped, and a term that repeats an earlier one, ignoring case, is kept once.
      </p>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => onFile(e.target.files?.[0])} className="text-sm text-gray-300" />
        <span className="text-xs text-gray-500">or</span>
        <textarea
          rows={2}
          placeholder="Paste terms, one per line"
          onChange={(e) => load(e.target.value, 'pasted', false)}
          className={`${inputClass} w-72 font-mono`}
        />
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-300">
          {width > 1 && (
            <label className="flex items-center gap-2">
              Column
              <select value={column} onChange={(e) => { setColumn(Number(e.target.value)); setPreview(null); }} className={inputClass}>
                {Array.from({ length: width }, (_, i) => (
                  <option key={i} value={i}>
                    {String.fromCharCode(65 + (i % 26))}{header?.[i] ? ` — ${header[i]}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={hasHeader} onChange={(e) => { setHasHeader(e.target.checked); setPreview(null); }} />
            First row is a header
          </label>
          <span className="text-gray-400">
            {terms.length.toLocaleString()} terms{source && source !== 'pasted' ? ` from ${source}` : ''}
            {terms.length > 0 && <span className="text-gray-500">, e.g. {terms.slice(0, 3).join(', ')}</span>}
          </span>
        </div>
      )}

      {problem && <div className="mt-3"><ErrorNote>{problem}</ErrorNote></div>}

      {preview && (
        <div className="mt-3 rounded border border-blue-800/60 bg-blue-900/10 px-4 py-3 text-sm text-gray-200 space-y-1">
          <p>
            The list would go from {preview.beforeCount.toLocaleString()} terms to {preview.afterCount.toLocaleString()}.
          </p>
          <TermSummary label="Add" terms={preview.added} total={preview.addedCount} />
          <TermSummary label="Remove" terms={preview.removed} total={preview.removedCount} />
          {!!preview.duplicatesCount && <TermSummary label="Repeated, kept once" terms={preview.duplicates ?? []} total={preview.duplicatesCount} />}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-3">
        <button onClick={() => onDone(null)} disabled={busy !== null} className={plainButton}>Cancel</button>
        {!preview ? (
          <button
            onClick={async () => setPreview(await send(true))}
            disabled={busy !== null || terms.length === 0}
            className={primaryButton}
          >
            {busy === 'preview' ? 'Checking…' : 'Preview change'}
          </button>
        ) : (
          <button onClick={save} disabled={busy !== null} className={primaryButton}>
            {busy === 'save' ? 'Replacing…' : 'Replace list'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Test check ──────────────────────────────────────────────────────────────

export function HarmCheckSection({ env }: { env: RlcEnvironment }) {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RlcScanResult | null>(null);
  const [sent, setSent] = useState<{ email: string; subject: string; body: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<'idle' | 'sending' | 'done'>('idle');
  const [reportError, setReportError] = useState<string | null>(null);
  const { mutate: mutateGlobal } = useSWRConfig();

  useEffect(() => {
    if (user?.email) setEmail((current) => current || user.email);
  }, [user?.email]);

  const sendReport = async () => {
    if (!result?.outcome || !sent) return;
    setReport('sending');
    setReportError(null);
    try {
      await adminApi.reportEnvironmentScan(env.key, {
        ...sent,
        correlationId: result.correlationId,
        responseTimeMs: result.latencyMs,
        outcome: result.outcome,
      });
      setReport('done');
      mutateGlobal((key) => Array.isArray(key) && typeof key[0] === 'string' && key[0].startsWith('admin/environments/metrics') && key[1] === env.key);
    } catch (err) {
      setReport('idle');
      setReportError(err instanceof Error ? err.message : 'The report could not be sent.');
    }
  };

  const run = async (e: React.FormEvent) => {
    e.preventDefault();
    setRunning(true);
    setError(null);
    setResult(null);
    setReport('idle');
    setReportError(null);
    try {
      const message = { email, subject, body };
      setResult(await adminApi.runEnvironmentScan(env.key, message));
      setSent(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The check could not be run.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Section title="Test a message">
      {!env.scanAllowed ? (
        <p className="text-sm text-gray-400">Test checks are turned off for this environment so they do not land in its logs and usage numbers.</p>
      ) : !env.restrictedListConnected ? (
        <NotConnected what="a test check" envKey={env.key} />
      ) : (
        <>
          <p className="text-sm text-gray-400 max-w-3xl">
            Sends a message to this environment's scan endpoint the same way the Outlook add-in does when you press
            Send, and shows what the add-in would get back. The environment logs the check and counts it under Scan
            requests on the <Link to={`/admin/environments/${env.key}/usage`} className="text-blue-400 hover:underline">Usage</Link> tab.
            It appears under Latest add-in events only if you then report its outcome, as the add-in would.
          </p>
          <form onSubmit={run} className="mt-3 space-y-3 max-w-3xl">
            <label className="block text-sm text-gray-300">
              From
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputClass} w-full mt-1`} />
              <span className="block text-xs text-gray-500 mt-1">The sender decides which organization's rules apply.</span>
            </label>
            <label className="block text-sm text-gray-300">
              Subject
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={`${inputClass} w-full mt-1`} />
            </label>
            <label className="block text-sm text-gray-300">
              Message
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className={`${inputClass} w-full mt-1`} />
            </label>
            <button type="submit" disabled={running || (!subject.trim() && !body.trim())} className={primaryButton}>
              {running ? 'Checking…' : 'Check message'}
            </button>
          </form>
          {error && <div className="mt-3"><ErrorNote>{error}</ErrorNote></div>}
          {result && <ScanResult result={result} />}
          {result?.outcome && (
            <div className="mt-4 border-t border-gray-800 pt-3 max-w-3xl">
              <p className="text-sm text-gray-400">
                After a scan the add-in reports the outcome back. Do the same for this check and it
                {result.outcome.eventType === 'validation_blocked'
                  ? ` is listed under Latest add-in events as a block (${result.outcome.blockReason}) and counts under Blocked.`
                  : ' counts under Sent clean. Clean sends are counted, not listed as events.'}
                {' '}It is marked as coming from the console.
              </p>
              <div className="flex items-center gap-3 mt-2">
                <button onClick={sendReport} disabled={report !== 'idle'} className={plainButton}>
                  {report === 'sending' ? 'Reporting…' : report === 'done' ? 'Reported' : 'Report the outcome like the add-in'}
                </button>
                {report === 'done' && <span className="text-xs text-emerald-300">Sent. The <Link to={`/admin/environments/${env.key}/usage`} className="underline">Usage</Link> tab has been refreshed.</span>}
              </div>
              {reportError && <div className="mt-2"><ErrorNote>{reportError}</ErrorNote></div>}
            </div>
          )}
        </>
      )}
    </Section>
  );
}

function ScanResult({ result }: { result: RlcScanResult }) {
  const r = result.response;
  if (!result.ok || !r) {
    return (
      <div className="mt-4">
        <ErrorNote>
          The scan endpoint answered {result.httpStatus ? `HTTP ${result.httpStatus}` : 'nothing'}
          {(r?.error || result.error) && `: ${r?.error || result.error}`}
        </ErrorNote>
      </div>
    );
  }

  const violations: RlcScanViolation[] = (r.violations ?? r.body ?? []).filter((v) => v.label && v.label !== 'neutral');
  const terms = r.foundRestrictedNames ?? [];
  const clean = violations.length === 0 && terms.length === 0;
  const flag = (v?: boolean) => (v === undefined ? 'not reported' : v ? 'on' : 'off');

  return (
    <div className="mt-4 space-y-3">
      <div
        className={`rounded border px-4 py-3 text-sm ${
          clean ? 'bg-emerald-900/20 border-emerald-700/60 text-emerald-100' : 'bg-amber-900/20 border-amber-700/60 text-amber-100'
        }`}
      >
        {clean
          ? 'Nothing flagged.'
          : `Flagged: ${terms.length} restricted ${terms.length === 1 ? 'term' : 'terms'}, ${violations.length} ${violations.length === 1 ? 'sentence' : 'sentences'}.`}
        <span className="text-xs opacity-70 ml-2">Answered in {result.latencyMs} ms</span>
      </div>

      {terms.length > 0 && (
        <div className="text-sm text-gray-300">
          Restricted terms found:{' '}
          {terms.map((t) => (
            <span key={t} className="inline-block px-2 py-0.5 mr-1.5 rounded bg-red-900/40 text-red-200 text-xs">{t}</span>
          ))}
        </div>
      )}

      {violations.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="py-1.5 pr-4 font-medium">Label</th>
              <th className="py-1.5 pr-4 font-medium">Source</th>
              <th className="py-1.5 font-medium">Sentence</th>
            </tr>
          </thead>
          <tbody>
            {violations.map((v, i) => (
              <tr key={i} className="border-b border-gray-800/60 align-top">
                <td className="py-1.5 pr-4 text-gray-100 whitespace-nowrap">{v.label}</td>
                <td className="py-1.5 pr-4 text-gray-400 text-xs whitespace-nowrap">{v.source || v.labelType || '—'}</td>
                <td className="py-1.5 text-gray-300">{v.sentence || v.text || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="text-xs text-gray-500">
        Reported by this environment for the sender's organization: restricted list {flag(r.RLEnabled)} · code detection{' '}
        {flag(r.codeDetectionEnabled)} · labels {r.labelsDisabled === undefined ? 'not reported' : r.labelsDisabled ? 'off' : 'on'} ·
        attachment check {flag(r.checkAttachment)}
      </p>

      <details className="text-xs text-gray-400">
        <summary className="cursor-pointer hover:text-gray-200">Full response</summary>
        <pre className="mt-2 max-h-96 overflow-auto bg-gray-950 border border-gray-800 rounded p-3 whitespace-pre-wrap break-all">
          {JSON.stringify(r, null, 2)}
        </pre>
      </details>
    </div>
  );
}

// ─── Usage ───────────────────────────────────────────────────────────────────

export function UsageSection({ env }: { env: RlcEnvironment }) {
  const [days, setDays] = useState(7);
  const { data, error } = useSWR(
    env.restrictedListConnected ? ['admin/environments/metrics', env.key, days] : null,
    () => adminApi.getEnvironmentMetrics(env.key, days),
    { keepPreviousData: true, revalidateOnFocus: false }
  );

  const o = data?.outcomes;
  const rq = data?.requests;
  const peak = Math.max(1, ...(rq?.byDay.map((d) => d.requests) ?? []));

  return (
    <Section title="Usage" badge={env.restrictedListConnected ? 'Live' : undefined}>
      {!env.restrictedListConnected ? (
        <NotConnected what="usage" envKey={env.key} />
      ) : error ? (
        <ErrorNote>Could not read usage: {error.message}</ErrorNote>
      ) : !data ? (
        <p className="text-sm text-gray-500">Reading usage…</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-gray-500">From this environment's own metrics endpoints.</p>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={inputClass} aria-label="Period">
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          {data.requestsError ? (
            <ErrorNote>Request volume is unavailable: {data.requestsError}</ErrorNote>
          ) : rq && (
            <div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat label="Scan requests" value={rq.totalRequests} />
                <Stat label="Busiest minute" value={rq.peakMinute?.requestCount} hint={rq.peakMinute ? new Date(rq.peakMinute.timestamp).toLocaleString() : undefined} />
                <Stat label="Sent with a token" value={rq.authGate?.withToken} />
                <Stat label="Sent without a token" value={rq.authGate?.withoutToken} hint={rq.authGate?.unmeasured ? `${rq.authGate.unmeasured} not measured` : undefined} />
              </div>
              {rq.byDay.length > 0 && (
                <div className="mt-3 space-y-1">
                  {rq.byDay.map((d) => (
                    <div key={d.date} className="flex items-center gap-3 text-xs">
                      <span className="w-20 text-gray-500 shrink-0">{d.date}</span>
                      <div className="flex-1 bg-gray-950 rounded h-3">
                        <div className="bg-blue-600 rounded h-3" style={{ width: `${Math.max(1, (d.requests / peak) * 100)}%` }} />
                      </div>
                      <span className="w-12 text-right text-gray-300 shrink-0">{d.requests.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {data.outcomesError ? (
            <ErrorNote>Send outcomes are unavailable: {data.outcomesError}</ErrorNote>
          ) : o && (
            <div>
              <h3 className="text-sm font-semibold text-gray-100 mb-2">What the add-in reported</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat label="Sent clean" value={o.cleanEmailsProcessed} />
                <Stat label="Blocked" value={o.blockedEmails} />
                <Stat label="Corrected after a block" value={o.correctedBlocks} />
                <Stat label="Errors" value={o.errors} hint={o.errorRate ? `${o.errorRate} of sends` : undefined} />
              </div>
            </div>
          )}

          <AddinEvents env={env} />
        </div>
      )}
    </Section>
  );
}

const EVENTS_PAGE = 50;
const EVENT_TYPES = ['validation_blocked', 'slow_processing_dialog', 'error'];

function AddinEvents({ env }: { env: RlcEnvironment }) {
  const [eventType, setEventType] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [skip, setSkip] = useState(0);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setUserEmail(emailInput.trim()); setSkip(0); }, 400);
    return () => clearTimeout(t);
  }, [emailInput]);

  const { data, error } = useSWR(
    ['admin/environments/metrics/events', env.key, eventType, userEmail, skip],
    () => adminApi.getEnvironmentMetricEvents(env.key, { limit: EVENTS_PAGE, skip, eventType, userEmail }),
    { keepPreviousData: true, revalidateOnFocus: false }
  );
  const events = data?.events ?? [];
  const total = data?.total ?? 0;

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-gray-100">Latest add-in events</h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
            Reported by installed Outlook add-ins after a send is held up: blocked, slow, or failed. Any date, newest
            first. A message checked from this page is listed only if you report its outcome there; otherwise it just
            counts under Scan requests.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={eventType}
            onChange={(e) => { setEventType(e.target.value); setSkip(0); }}
            className={inputClass}
            aria-label="Event type"
          >
            <option value="">All events</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input
            type="text"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="Sender address (exact)"
            className={`${inputClass} w-56`}
          />
        </div>
      </div>

      {error ? (
        <div className="mt-2"><ErrorNote>Could not read add-in events: {error.message}</ErrorNote></div>
      ) : data?.error ? (
        <div className="mt-2"><ErrorNote>Add-in events are unavailable: {data.error}</ErrorNote></div>
      ) : !data ? (
        <p className="text-sm text-gray-500 mt-2">Reading events…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-500 mt-2">No events{eventType || userEmail ? ' match' : ' reported yet'}.</p>
      ) : (
        <>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-gray-400 text-left">
                  <th className="py-1.5 pr-4 font-medium">When</th>
                  <th className="py-1.5 pr-4 font-medium">Sender</th>
                  <th className="py-1.5 pr-4 font-medium">Event</th>
                  <th className="py-1.5 pr-4 font-medium">Reason</th>
                  <th className="py-1.5 pr-4 font-medium">Restricted terms</th>
                  <th className="py-1.5 pr-4 font-medium">Subject</th>
                  <th className="py-1.5 pr-4 font-medium text-right">Response</th>
                  <th className="py-1.5 font-medium">Outlook</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => {
                  const id = ev._id ?? String(i);
                  const isOpen = open === id;
                  return (
                    <EventRows key={id} ev={ev} isOpen={isOpen} onToggle={() => setOpen(isOpen ? null : id)} />
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-400 mt-2">
            <span>
              Showing {(skip + 1).toLocaleString()}–{(skip + events.length).toLocaleString()} of {total.toLocaleString()}. Select a row for the full report.
            </span>
            <div className="flex gap-2">
              <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - EVENTS_PAGE))} className={plainButton}>Newer</button>
              <button disabled={skip + events.length >= total} onClick={() => setSkip(skip + EVENTS_PAGE)} className={plainButton}>Older</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function EventRows({ ev, isOpen, onToggle }: { ev: RlcMetricEvent; isOpen: boolean; onToggle: () => void }) {
  const outcome = [ev.userDecision, ev.corrected ? 'corrected' : null].filter(Boolean).join(', ');
  const list = (v?: string[]) => (v && v.length > 0 ? v.join(', ') : '—');
  return (
    <>
      <tr
        onClick={onToggle}
        aria-expanded={isOpen}
        className={`border-b border-gray-800/60 align-top cursor-pointer hover:bg-gray-800/40 ${isOpen ? 'bg-gray-800/40' : ''}`}
      >
        <td className="py-1.5 pr-4 text-gray-500 text-xs whitespace-nowrap">{ev.createdAt ? new Date(ev.createdAt).toLocaleString() : '—'}</td>
        <td className="py-1.5 pr-4 text-gray-200 text-xs whitespace-nowrap">{ev.userEmail || '—'}</td>
        <td className="py-1.5 pr-4 text-gray-200 text-xs whitespace-nowrap">
          {ev.eventType || '—'}
          {outcome && <div className="text-gray-500">{outcome}</div>}
        </td>
        <td className="py-1.5 pr-4 text-gray-400 text-xs">{ev.blockReason || ev.errorCode || '—'}</td>
        <td className="py-1.5 pr-4 text-gray-300 text-xs max-w-[14rem] truncate" title={list(ev.restrictedTerms)}>{list(ev.restrictedTerms)}</td>
        <td className="py-1.5 pr-4 text-gray-300 text-xs max-w-[16rem] truncate" title={ev.emailSubject ?? undefined}>{ev.emailSubject || '—'}</td>
        <td className="py-1.5 pr-4 text-gray-400 text-xs whitespace-nowrap text-right">{ev.responseTimeMs != null ? `${Math.round(ev.responseTimeMs).toLocaleString()} ms` : '—'}</td>
        <td className="py-1.5 text-gray-500 text-xs max-w-[11rem] truncate" title={[ev.platform, ev.outlookClient].filter(Boolean).join(' · ')}>{[ev.platform, ev.outlookClient].filter(Boolean).join(' · ') || '—'}</td>
      </tr>
      {isOpen && (
        <tr className="border-b border-gray-800/60 bg-gray-950">
          <td colSpan={8} className="px-3 py-3">
            <dl className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 text-xs">
              <Detail label="Sender" value={ev.userEmail} />
              <Detail label="Recipients" value={list(ev.recipients)} />
              <Detail label="Subject" value={ev.emailSubject} />
              <Detail label="Attachments" value={list(ev.attachmentNames)} />
              <Detail label="Restricted terms" value={list(ev.restrictedTerms)} />
              <Detail label="Block reason" value={ev.blockReason} />
              <Detail label="What the sender did" value={outcome || null} />
              <Detail label="Outlook" value={[ev.platform, ev.outlookClient].filter(Boolean).join(' · ') || null} />
              <Detail label="Last updated" value={ev.updatedAt ? new Date(ev.updatedAt).toLocaleString() : null} />
              <Detail label="Error" value={[ev.errorCode, ev.httpStatus ? `HTTP ${ev.httpStatus}` : null, ev.errorMessage].filter(Boolean).join(' · ') || null} />
              <Detail label="Draft id" value={ev.draftId} mono />
              <Detail label="Correlation id" value={ev.correlationId} mono />
              <Detail label="Event id" value={ev._id} mono />
            </dl>
            {ev.eventType === 'validation_blocked' && <PopupShown reconstruction={ev.reconstruction} />}
            {ev.emailBody && (
              <div className="mt-2 text-xs">
                <div className="text-gray-500">Message</div>
                <pre className="mt-1 max-h-60 overflow-auto bg-gray-900 border border-gray-800 rounded p-2 text-gray-300 whitespace-pre-wrap break-words font-sans">{ev.emailBody}</pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// Why the API server could not rebuild a pop-up (helpers/popupReconstruction.js in web-restricted-list).
const POPUP_UNAVAILABLE: Record<string, string> = {
  no_inputs: 'The scan did not store what the pop-up was built from (it predates that, or belongs to another organization).',
  no_correlation_id: 'The block record has no correlation id to match it to its scan.',
  unsupported_addin_version: 'The add-in build is older than 2.1.0, whose pop-up cannot be reproduced exactly.',
  unsupported_format_version: 'The stored pop-up format is one this API server does not know.',
  no_popup_reconstructed: 'The stored inputs rebuild to no pop-up, so it cannot be said that one was shown.',
  error: 'The API server failed while rebuilding it; see its logs.',
};

/**
 * The block dialog the sender saw, rebuilt by the environment's API server from
 * what the scan stored. Never invented: if it cannot be rebuilt exactly, it says why.
 */
function PopupShown({ reconstruction: r }: { reconstruction?: RlcPopupReconstruction }) {
  let body: React.ReactNode;
  if (!r) {
    body = <p className="text-gray-500">This environment's API server does not rebuild pop-ups.</p>;
  } else if (r.status !== 'ok' || !r.message) {
    body = <p className="text-gray-400">Not available: {POPUP_UNAVAILABLE[r.reason ?? ''] ?? r.reason ?? 'unknown reason'}</p>;
  } else {
    const hidden = (r.sources ?? []).reduce((n, s) => n + s.hidden.length, 0);
    const notes = [
      hidden > 0 && `${hidden} more term${hidden === 1 ? '' : 's'} found but cut to fit Outlook's 500-character limit`,
      (r.droppedSources?.length ?? 0) > 0 && `${r.droppedSources!.length} more source${r.droppedSources!.length === 1 ? '' : 's'} left out for space`,
      r.formatVersionSource === 'assumed' && 'the add-in did not report its version, so its format was assumed',
    ].filter(Boolean);
    body = (
      <>
        <div className="max-w-xl bg-gray-900 border border-gray-700 rounded p-3 text-gray-100 whitespace-pre-wrap break-words font-sans text-sm">
          {r.message.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, i) =>
            /^\*\*[^*]+\*\*$/.test(part) ? <strong key={i}>{part.slice(2, -2)}</strong>
              : /^\*[^*]+\*$/.test(part) ? <em key={i}>{part.slice(1, -1)}</em>
              : <span key={i}>{part}</span>
          )}
        </div>
        <div className="text-gray-500 mt-1">
          Rebuilt by the API server from what the scan stored · format {r.formatVersion}, wording {r.copyVersion}
          {r.addinVersion && `, add-in ${r.addinVersion}`}
          {notes.length > 0 && <div>Note: {notes.join('; ')}.</div>}
        </div>
      </>
    );
  }
  return (
    <div className="mt-2 text-xs">
      <div className="text-gray-500 mb-1">Pop-up the sender saw</div>
      {body}
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div className="flex gap-3 py-1 border-t border-gray-800/60">
      <dt className="w-36 shrink-0 text-gray-500">{label}</dt>
      <dd className={`text-gray-200 min-w-0 break-words ${mono ? 'font-mono' : ''}`}>{value || '—'}</dd>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value?: number | null; hint?: string }) {
  return (
    <div className="bg-gray-950 border border-gray-800 rounded px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-semibold text-gray-100 mt-0.5">{value == null ? '—' : value.toLocaleString()}</div>
      {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
}
